import { Button, Image, Space } from 'antd';
import axios from 'axios';
import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';

import dayjs from 'dayjs';
import OrderNoteCount from '../../../components/orderNoteCount';
import OrderLatestNote from '../../../components/orderLatestNote';
import PPTableLink from '../../../components/link/PPTableLink';
import PPPageHeader from '../../../components/pageHeader/PPPageHeader';
import ProductImage from '../../../components/productImage';
import ColumnSettings from '../../../components/table/ColumnSettings';
import PPResizableTable from '../../../components/table/PPResizableTable';
import { useColumnSettings } from '../../../components/table/useColumnSettings';
import { useTableFilter } from '../../../utils/Filter';
import { getIndexColumn } from '../../../utils/getIndexColumn';
import { PlusOutlined } from '@ant-design/icons';

import '../Status.less';

const routes = [
  {
    path: '/order',
    breadcrumbName: 'B2C/B2B захиалга',
  },
  {
    path: 'first',
    breadcrumbName: 'Буцаалтын захиалга',
  },
];

// Барааны мэдээлэл баганад харуулах зурагны тоо (үлдсэнийг "+N" гэж харуулна)
const VISIBLE_IMAGE_COUNT = 5;

// meta-гээс ирсэн enum-ыг enumDictionary-аар нэрлэж шүүлтүүрийн сонголт болгох
// (enumDictionary дээр нэр нь байхгүй утгуудыг харуулахгүй)
const getEnumFilters = (field) =>
  (field?.enum ?? [])
    .filter((value) => field?.enumDictionary?.[value])
    .map((value) => ({
      text: field.enumDictionary[value],
      value,
    }));

const getColumns = (
  location,
  getColumnSearchProps,
  getColumnFilterProps,
  pagination,
  meta,
  returnReasonEnum
) => [
  getIndexColumn(pagination.current, pagination.pageSize),
  {
    title: 'Захиалгын дугаар',
    dataIndex: 'code',
    key: 'code',
    ...getColumnSearchProps('code', 'Захиалгын дугаар'),
    render: (text, record) => (
      <PPTableLink
        to={{
          pathname: `/merchant/order/returns/${record._id}`,
          state: {
            search: location.search,
            // Дэлгэрэнгүй дээр төлвийн нэр, буцаах шалтгааныг нэрлэхэд хэрэглэнэ
            result: { meta, returnReasonEnum },
          },
        }}
      >
        {text}
      </PPTableLink>
    ),
    width: 200,
  },
  {
    title: 'Утасны дугаар',
    dataIndex: 'customerPhone',
    key: 'customerPhone',
    ...getColumnSearchProps('customerPhone', 'Утасны дугаар'),
    width: 200,
  },
  {
    title: 'Барааны мэдээлэл',
    key: 'productImages',
    width: 200,
    render: (_, record) =>
      record?.productImages?.length ? (
        <Image.PreviewGroup>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {record.productImages.slice(0, VISIBLE_IMAGE_COUNT).map((child, index) => (
              <ProductImage
                key={index}
                src={child}
                alt={`Барааны зураг ${index + 1}`}
                size={40}
                preview
                style={{
                  objectFit: 'cover',
                  borderRadius: 6,
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
                }}
              />
            ))}
            {record.productImages.length > VISIBLE_IMAGE_COUNT && (
              <div
                style={{
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 6,
                  background: '#f0f0f0',
                  fontSize: 12,
                  fontWeight: 500,
                  color: '#595959',
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
                }}
              >
                +{record.productImages.length - VISIBLE_IMAGE_COUNT}
              </div>
            )}
          </div>
        </Image.PreviewGroup>
      ) : (
        '-'
      ),
  },
  {
    title: 'Тэмдэглэл',
    dataIndex: 'numberOfNotes',
    key: 'numberOfNotes',
    render: (count) => <OrderNoteCount count={count} />,
    width: 200,
  },
  {
    title: 'Сүүлийн тэмдэглэл',
    dataIndex: 'orderNote',
    key: 'orderNote',
    render: (_, record) => <OrderLatestNote note={record.latestNote} />,
    width: 200,
  },
  {
    title: 'Төлөв',
    dataIndex: 'orderStatus',
    key: 'orderStatus',
    width: 200,
    filters: getEnumFilters(meta?.orderStatus),
    // Нэг л төлөв сонгоно (radio)
    ...getColumnFilterProps('orderStatus', { multiple: false }),
    render: (orderStatus, record) =>
      orderStatus ? (
        <div className="return-status-cell">
          <span className={`return-status-badge return-status-${orderStatus}`}>
            <span className="return-status-dot" />
            {meta?.orderStatus?.enumDictionary?.[orderStatus] ?? orderStatus}
          </span>
          {record?.orderStatusDate ? (
            <span className="return-status-date">
              {dayjs(record.orderStatusDate).format('YYYY-MM-DD HH:mm:ss')}
            </span>
          ) : (
            '-'
          )}
        </div>
      ) : (
        '-'
      ),
  },
];

const List = () => {
  const location = useLocation();
  const history = useHistory();
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState({});
  const [returnReasonEnum, setReturnReasonEnum] = useState({});
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchData = async ({ page = 1, pageSize = 10, ...filters } = {}) => {
    setLoading(true);
    try {
      const { data } = await axios.post('/api/v1.0/request/list-return', {
        page,
        pageSize,
        ...filters,
      });
      setData(data?.result?.docs || []);
      setMeta(data?.result?.meta || {});
      setReturnReasonEnum(data?.result?.returnReasonEnum || {});
      setPagination({
        current: data?.result?.page ?? page,
        pageSize: data?.result?.limit ?? pageSize,
        total: data?.result?.totalDocs ?? 0,
      });
    } catch (error) {
      console.error('fetchReturnList: ', error);
    } finally {
      setLoading(false);
    }
  };

  const { getColumnSearchProps, getColumnFilterProps, handleTableChange, withHighlight } =
    useTableFilter({
      fetchData,
    });

  const { columns, settingsProps, onColumnOrderChange } = useColumnSettings({
    columns: withHighlight(
      getColumns(
        location,
        getColumnSearchProps,
        getColumnFilterProps,
        pagination,
        meta,
        returnReasonEnum
      )
    ),
    storageKey: 'orderReturn.list',
  });

  return (
    <>
      <PPPageHeader
        title="Буцаалтын захиалга"
        breadcrumb={{ routes }}
        extra={
          <Space>
            <ColumnSettings {...settingsProps} />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => history.push('/order/return/add')}
            >
              Шинэ захиалга
            </Button>
          </Space>
        }
      />
      <div className="pp_table_wrapper">
        <PPResizableTable
          dataSource={data}
          columns={columns}
          onColumnOrderChange={onColumnOrderChange}
          size="middle"
          rowKey="_id"
          loading={loading}
          onChange={handleTableChange}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Нийт ${total} илэрц`,
          }}
          scroll={{ x: 2000 }}
        />
      </div>
    </>
  );
};

export default List;
